import PropTypes from "prop-types";

import {
  DateRangeFiltersInput,
  DropdownInput,
  FlexContainers,
  WhiteContainer
} from "../../../../../components";
import { strCode } from "../../../../../consts";

import { useDashboardFilterOptions, useSelectedDashboardFilters } from "../../../../../hooks";
import { t } from "../../../../../services";

import { Filter, FilterSection, FilterSectionsWrapper, FilterSectionTitle } from "./styled";

const FiltersView = ({
  onUpdateDevice,
  onUpdateLocation,
  onUpdatePlatform,
  onUpdateMarket,
  onUpdateBrowser,
  onUpdateDateRange,
  onUpdateAppType,
  onUpdateGroup,
  onUpdateConnectivity,
  isLoading,
  isMarketAdminOnly,
  isMarketSelectorDisabled,
  marketDefaultOptionsId,
  isLocationSelectorDisabled,
  locationsDefaultOptionsId
}) => {
  const {
    deviceOptions,
    marketOptions,
    locationOptions,
    platformOptions,
    browserOptions,
    appTypesOptions,
    groupsOptions,
    connectivityOptions
  } = useDashboardFilterOptions();

  const {
    selectedDevice,
    selectedLocation,
    selectedMarkets,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity
  } = useSelectedDashboardFilters();

  return (
    <FilterSectionsWrapper Container={FlexContainers.FlexRowCenter} gap={16} alignItems="end">
      <FilterSection Container={WhiteContainer}>
        <FilterSectionTitle>{t(strCode.DASHBOARD_FILTERS_SECTION)}</FilterSectionTitle>
        {!isMarketAdminOnly && (
          <Filter>
            <DropdownInput
              id="group-select"
              title={t(strCode.CHARTS_TITLE)}
              options={groupsOptions}
              selectedOptions={selectedGroup ?? []}
              onSelectOption={onUpdateGroup}
              disabled={isLoading}
              defaultOptionId={0}
              hasError={selectedGroup && !selectedGroup?.length}
              showCheckboxes
              isSmall
            />
          </Filter>
        )}
        <Filter>
          <DropdownInput
            id="device-select"
            title={t(strCode.DEVICE_TITLE)}
            options={deviceOptions}
            selectedOption={selectedDevice}
            onSelectOption={onUpdateDevice}
            disabled={isLoading}
            isSmall
          />
        </Filter>
        <Filter>
          <DateRangeFiltersInput
            title={t(strCode.DATE_RANGE_TITLE)}
            loading={isLoading}
            onUpdateDateRange={onUpdateDateRange}
          />
        </Filter>
      </FilterSection>

      <FilterSection Container={WhiteContainer}>
        <FilterSectionTitle>{t(strCode.APP_AND_MARKET_FILTERS_SECTION)}</FilterSectionTitle>
        {appTypesOptions.length > 2 && (
          <Filter>
            <DropdownInput
              id="app-type-select"
              title={t(strCode.APPLICATION_TITLE)}
              options={appTypesOptions}
              selectedOptions={selectedAppType ?? []}
              onSelectOption={onUpdateAppType}
              disabled={isLoading}
              defaultOptionId={0}
              hasError={selectedAppType && !selectedAppType?.length}
              showCheckboxes
              isSmall
            />
          </Filter>
        )}
        <Filter>
          <DropdownInput
            id="market-select"
            title={t(strCode.MARKET_TITLE)}
            options={marketOptions}
            selectedOptions={selectedMarkets ?? []}
            onSelectOption={onUpdateMarket}
            disabled={isMarketSelectorDisabled}
            defaultOptionId={marketDefaultOptionsId}
            hasError={selectedMarkets && !selectedMarkets?.length}
            showCheckboxes
            isSmall
          />
        </Filter>
        <Filter>
          <DropdownInput
            id="location-select"
            title={t(strCode.LOCATION_TITLE)}
            options={locationOptions}
            selectedOptions={selectedLocation ?? []}
            onSelectOption={onUpdateLocation}
            disabled={isLocationSelectorDisabled}
            defaultOptionId={locationsDefaultOptionsId}
            hasError={selectedLocation && !selectedLocation?.length}
            showCheckboxes
            isSmall
          />
        </Filter>
      </FilterSection>

      <FilterSection Container={WhiteContainer}>
        <FilterSectionTitle>
          {t(strCode.PLATFORM_AND_CONNECTIVITY_FILTERS_SECTION)}
        </FilterSectionTitle>
        <Filter>
          <DropdownInput
            id="platform-select"
            title={t(strCode.OPERATING_SYSTEM_TITLE)}
            options={platformOptions}
            selectedOptions={selectedPlatform ?? []}
            onSelectOption={onUpdatePlatform}
            disabled={isLoading}
            defaultOptionId={0}
            hasError={selectedPlatform && !selectedPlatform?.length}
            showCheckboxes
            isSmall
          />
        </Filter>

        <Filter>
          <DropdownInput
            id="browser-select"
            title={t(strCode.BROWSER_TITLE)}
            options={browserOptions}
            selectedOptions={selectedBrowser ?? []}
            onSelectOption={onUpdateBrowser}
            disabled={isLoading}
            defaultOptionId={0}
            hasError={selectedBrowser && !selectedBrowser?.length}
            showCheckboxes
            isSmall
          />
        </Filter>

        <Filter>
          <DropdownInput
            id="connectivity-select"
            title={t(strCode.CONNECTIVITY_TITLE)}
            options={connectivityOptions}
            selectedOptions={selectedConnectivity ?? []}
            onSelectOption={onUpdateConnectivity}
            disabled={isLoading}
            defaultOptionId={0}
            hasError={selectedConnectivity && !selectedConnectivity?.length}
            showCheckboxes
            isSmall
          />
        </Filter>
      </FilterSection>
    </FilterSectionsWrapper>
  );
};

FiltersView.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isMarketAdminOnly: PropTypes.bool.isRequired,
  isMarketSelectorDisabled: PropTypes.bool.isRequired,
  marketDefaultOptionsId: PropTypes.number.isRequired,
  isLocationSelectorDisabled: PropTypes.bool.isRequired,
  locationsDefaultOptionsId: PropTypes.number.isRequired,

  onUpdateDevice: PropTypes.func.isRequired,
  onUpdateMarket: PropTypes.func.isRequired,
  onUpdateLocation: PropTypes.func.isRequired,
  onUpdatePlatform: PropTypes.func.isRequired,
  onUpdateBrowser: PropTypes.func.isRequired,
  onUpdateDateRange: PropTypes.func.isRequired,
  onUpdateAppType: PropTypes.func.isRequired,
  onUpdateGroup: PropTypes.func.isRequired,
  onUpdateConnectivity: PropTypes.func.isRequired
};

export default FiltersView;
